import { FIRST_LAYER_VARIANT, LogoPosition, UI_VARIANT } from '@usercentrics/cmp-browser-sdk';
import { getCssBasedOnRtl, media } from '../../styles';
import styled, { DefaultTheme } from 'styled-components';
import { CSS_PROPERTY } from '../../enums/Css';
import { VIEW } from '../../enums';

type HeaderWrapperProps = {
  firstLayerVariant: FIRST_LAYER_VARIANT | null;
  noPadding?: boolean;
  uiVariant?: UI_VARIANT;
  view?: VIEW;
};

interface LogoProps {
  position: LogoPosition;
}

export const HeaderWrapper = styled.div<HeaderWrapperProps>`
  background-color: ${({ theme }) => theme.ui.layer.backgroundColor};
  display: block;
  flex: initial;
  flex-shrink: 0;
  padding: ${({ noPadding, theme }) =>
    noPadding ? '0' : `${theme.spacing.base.xl} ${theme.spacing.base.xl} ${theme.spacing.base.sm}`};

  ${media.mobilePortrait} {
    padding: ${({ noPadding, theme }) => (noPadding ? '0' : theme.spacing.mobile.xxxl)};
  }

  ${media.ie11} {
    max-width: ${({ view, firstLayerVariant }) =>
      (view === VIEW.FIRST_LAYER && firstLayerVariant === FIRST_LAYER_VARIANT.WALL) || view === VIEW.SECOND_LAYER
        ? '625px'
        : 'initial'};
  }
`;

export const HeaderContainer = styled.div`
  color: ${({ theme }) => theme.typography.color.primary};
  display: flex;
  flex: 1;
  flex-shrink: 0;
  flex-direction: column;
`;

export const TopRow = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
`;

export const handleNavPosition = (pos: LogoPosition, theme: DefaultTheme): string => {
  switch (pos) {
    case 'center': {
      return `${getCssBasedOnRtl(theme, CSS_PROPERTY.POSITION_RIGHT, 0)};
      ${getCssBasedOnRtl(theme, CSS_PROPERTY.FLEX_ALIGNMENT_RIGHT, 0)};`;
    }
    case 'left': {
      return `${getCssBasedOnRtl(theme, CSS_PROPERTY.POSITION_RIGHT, 0)};
      ${getCssBasedOnRtl(theme, CSS_PROPERTY.FLEX_ALIGNMENT_RIGHT, 0)};`;
    }
    default:
    case 'right': {
      return `${getCssBasedOnRtl(theme, CSS_PROPERTY.POSITION_LEFT, 0)};
      ${getCssBasedOnRtl(theme, CSS_PROPERTY.FLEX_ALIGNMENT_LEFT, 0)};`;
    }
  }
};

export const OptionsContainer = styled.div<LogoProps>`
  display: flex;
  position: absolute;
  ${({ position, theme }) => handleNavPosition(position, theme)}
  width: calc(100% - 300px);

  ${media.mobilePortraitXS} {
    width: 80%;
  }
`;

interface ButtonProps {
  height: string;
}

export const Button = styled.button<ButtonProps>`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  height: ${({ height }) => height};
  outline: none;
  ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_LEFT, theme.spacing.base.md)};
  ${media.mobilePortraitXS} {
    ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_LEFT, theme.spacing.mobile.md)};
    padding: 0;
  }

  &:focus-visible {
    outline-style: auto;
    outline-width: 5px;
  }
`;

const handleLogoMargin = (pos: LogoPosition, theme: DefaultTheme): string => {
  switch (pos) {
    case 'center': {
      return 'margin: 0 auto';
    }
    case 'right': {
      return getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_LEFT, 'auto');
    }
    default:
    case 'left': {
      return getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_RIGHT, 'auto');
    }
  }
};

export const LogoContainer = styled.div<LogoProps>`
  height: 48px;
  max-height: 48px;
  max-width: 300px;
  width: 300px;
  ${({ position, theme }) => handleLogoMargin(position, theme)};

  ${media.mobilePortraitXS} {
    max-width: 180px;
    width: 180px;
  }
`;

const handleLogoPosition = (pos: LogoPosition, theme: DefaultTheme): string => {
  switch (pos) {
    case 'center': {
      return CSS_PROPERTY.OBJECT_POSITION_CENTER;
    }
    case 'right': {
      return getCssBasedOnRtl(theme, CSS_PROPERTY.OBJECT_POSITION_RIGHT);
    }
    default:
    case 'left': {
      return getCssBasedOnRtl(theme, CSS_PROPERTY.OBJECT_POSITION_LEFT);
    }
  }
};

export const Logo = styled.img<LogoProps>`
  height: 100%;
  object-fit: contain;
  ${({ position, theme }) => handleLogoPosition(position, theme)};
  width: 100%;

  ${media.ie11} {
    /* IE10+ CSS styles go here */
    height: auto;
    width: auto;
    max-height: 48px;
    max-width: 300px;
  }
`;
